import { useEffect, useState, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

/**
 * FloatingScrollNav — luxury glass floating scroll controls.
 *
 * Visibility:
 *   - Hidden while scrollY <= 300 (near the very top).
 *   - Fades in past 300 px and follows the user.
 *
 * Buttons (based on scroll position within the page):
 *   - Near top (0-25%): only Down button
 *   - Middle (25%-75%): both Up + Down
 *   - Near bottom (>75%): only Up button
 *
 * Interactions:
 *   - Smooth scroll by 50% of viewport height per click.
 *   - Hover: gold glow + slight scale.
 *   - Keyboard-accessible with aria-labels + focus ring.
 */
const FloatingScrollNav = () => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState("top"); // top | middle | bottom

  const compute = useCallback(() => {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const winH = window.innerHeight || doc.clientHeight || 0;
    const docH = Math.max(doc.scrollHeight, doc.offsetHeight, doc.clientHeight);
    const maxScroll = Math.max(docH - winH, 1);
    const pct = scrollTop / maxScroll;

    setVisible(scrollTop > 300);
    if (pct < 0.25) setPosition("top");
    else if (pct > 0.75) setPosition("bottom");
    else setPosition("middle");
  }, []);

  useEffect(() => {
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [compute]);

  const scrollBy = (direction) => {
    const delta = window.innerHeight * 0.5 * (direction === "up" ? -1 : 1);
    window.scrollBy({ top: delta, left: 0, behavior: "smooth" });
  };

  const showUp = position !== "top";
  const showDown = position !== "bottom";

  return (
    <div
      aria-hidden={!visible}
      data-testid="floating-scroll-nav"
      className={`fixed z-40 right-4 md:right-6 bottom-24 md:bottom-8 flex flex-col gap-2 items-center transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"
      }`}
    >
      {showUp && (
        <button
          type="button"
          onClick={() => scrollBy("up")}
          aria-label="Scroll up half a page"
          data-testid="scroll-nav-up"
          className="ft-scroll-btn"
        >
          <ChevronUp size={18} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
      {showDown && (
        <button
          type="button"
          onClick={() => scrollBy("down")}
          aria-label="Scroll down half a page"
          data-testid="scroll-nav-down"
          className="ft-scroll-btn"
        >
          <ChevronDown size={18} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default FloatingScrollNav;
